import { NavLink } from 'react-router';
import { useQuotincLink } from './quotinc.api';
import { QUOTINC_TOUR_TARGET, useMarkQuotincOpened } from '@/features/onboarding/QuotincOnboarding';

/** The words behind the entry: its tooltip and the Help section's lead. */
export const QUOTINC_NAV_HELP =
  "Quotinc is where your company's plans, billing and team access live. " +
  'Open it to invite people or to choose who uses Quotinc, Amassa or both. ' +
  'Which Amassa modules each person sees stays here, in Admin > Users.';

/** Preferences > Help, the Quotinc section. */
export const QUOTINC_HELP_TO = '/preferences/help#quotinc';

/**
 * Amassa's "Quotinc" entry, at the foot of the sidebar above the person,
 * apart from the module rows. Only while this install is linked to a
 * Quotinc company; it opens Quotinc (`Quotinc:BaseUrl + /app`) in a new tab.
 * Quotinc keeps its own sign-in, so there is no token on this hop.
 *
 * Quotinc is the cobalt sibling: a cobalt hairline and mark, never a fill.
 */
export function QuotincNavEntry({ collapsed }: { collapsed: boolean }) {
  const link = useQuotincLink();
  const markOpened = useMarkQuotincOpened();
  if (!link.on || !link.appUrl) return null;

  if (collapsed) {
    return (
      <a
        href={link.appUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="hnav__quotinc hnav__quotinc--tile"
        data-tour={QUOTINC_TOUR_TARGET}
        onClick={markOpened}
        aria-label="Open Quotinc in a new tab"
        title={`Quotinc. ${QUOTINC_NAV_HELP}`}
      >
        <QuotincGlyph />
      </a>
    );
  }

  return (
    <div className="hnav__quotinc-wrap" data-tour={QUOTINC_TOUR_TARGET}>
      <a
        href={link.appUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="hnav__quotinc"
        title={QUOTINC_NAV_HELP}
        onClick={markOpened}
      >
        <QuotincGlyph />
        <span className="hnav__quotinc-label">Quotinc</span>
        <span className="hnav__quotinc-sub">plans, billing, team</span>
        <svg className="hnav__quotinc-out" width="11" height="11" viewBox="0 0 10 10" aria-hidden>
          <path
            d="M4 2 H8 V6 M8 2 L3 7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
      <NavLink
        to={QUOTINC_HELP_TO}
        className="hnav__quotinc-help"
        aria-label="What is Quotinc?"
        title="What is Quotinc?"
      >
        ?
      </NavLink>
    </div>
  );
}

/** A small cobalt square with the bottom-left house corner, a "Q" cut in it. */
function QuotincGlyph() {
  return (
    <span className="hnav__quotinc-mark" aria-hidden>
      Q
    </span>
  );
}
